import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { AdminUserListItem, UserRole } from '@proref/types';
import { PageHeader } from '../../components/shared/PageHeader';
import { Icon } from '../../components/shared/Icons';
import { Input } from '../../components/shared/Input';
import { Avatar } from '../../components/shared/Avatar';
import { ListSkeleton } from '../../components/shared/Skeleton';
import { DataPagination } from '../../components/shared/DataPagination';
import { usePagination } from '../../hooks/use-pagination';
import { useUsersQuery } from '../../hooks/use-users';
import { FilterSelect, type FilterOption } from '../../components/shared/FilterSelect';
import { InviteUserDialog } from '../../components/admin/InviteUserDialog';
import { BulkInviteDialog } from '../../components/admin/BulkInviteDialog';
import { EditUserDialog } from '../../components/admin/EditUserDialog';
import { RemoveUserDialog } from '../../components/admin/RemoveUserDialog';
import { apiErrorMessage } from '../../lib/api';

const USERS_PAGE_SIZE = 15;

// Role → human label + chip colour. super_admin is the global operator, so it reads differently
// from the tenant-scoped roles. A function (not a static map) since the label is translated —
// shared with FederationUsersPage's roster table.
export function roleMeta(t: TFunction, role: UserRole): { label: string; chip: string } {
  const chip: Record<UserRole, string> = {
    referee: 'bg-bg-2 text-ink-2 border-line-2',
    federation_admin: 'bg-accent-tint text-accent border-accent/30',
    super_admin: 'bg-warn-tint text-warn border-warn/30',
  };
  return { label: t(`admin.users.roleLabel.${role}`), chip: chip[role] };
}

// Build a filter's options from the rows themselves: one entry per distinct id actually present,
// labelled by the name that came with it, alphabetical. Rows missing the id (a super_admin has no
// sport or federation) contribute nothing.
function optionsFrom(
  users: AdminUserListItem[],
  pick: (u: AdminUserListItem) => [string | null, string | null],
  t: TFunction,
): FilterOption[] {
  const seen = new Map<string, string>();
  for (const u of users) {
    const [id, name] = pick(u);
    if (id && !seen.has(id)) seen.set(id, name ?? t('admin.users.list.unknownFallback'));
  }
  return Array.from(seen.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

// The super_admin "all users" dashboard — every referee/admin on the platform, across tenants,
// and the one place ProRef manages accounts end to end: add someone to any federation (an
// invite — signup stays invite-only, so they set their own password), edit them, remove them.
// Filtered by a free text search (name/email), role, sport and federation, then paginated
// client-side.
export function UsersPage() {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useUsersQuery();
  const users = useMemo(() => data ?? [], [data]);

  const [search, setSearch] = useState('');
  const [role, setRole] = useState<'all' | UserRole>('all');
  const [sportId, setSportId] = useState('all');
  const [federationId, setFederationId] = useState('all');

  // Options come from the roster itself, not the full sports/federations catalogues: a tenant with
  // no members would otherwise sit in the dropdown and always filter to nothing. Same rule the
  // super_admin clips console uses for its federation filter.
  const sportOptions = useMemo(() => optionsFrom(users, (u) => [u.sport_id, u.sport_name], t), [users, t]);
  const federationOptions = useMemo(
    () => optionsFrom(users, (u) => [u.federation_id, u.federation_name], t),
    [users, t],
  );

  // Filter in memory — the list is the whole platform roster but small enough that a client-side
  // filter is instant and avoids a round-trip per keystroke.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (role !== 'all' && u.role !== role) return false;
      // A super_admin row has neither, so picking any sport/federation correctly drops it.
      if (sportId !== 'all' && u.sport_id !== sportId) return false;
      if (federationId !== 'all' && u.federation_id !== federationId) return false;
      if (!q) return true;
      return u.full_name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
    });
  }, [users, search, role, sportId, federationId]);

  const { page, setPage, pageCount, pageItems } = usePagination(filtered, USERS_PAGE_SIZE);

  // usePagination only CLAMPS when the list shrinks, which would leave you on page 3 of a freshly
  // narrowed set — go back to the first page whenever a filter changes, so the rows you asked for
  // are the rows you see.
  useEffect(() => {
    setPage(1);
  }, [search, role, sportId, federationId, setPage]);

  return (
    <>
      <PageHeader
        title={t('admin.users.list.title')}
        sub={t('admin.users.list.sub')}
        actions={
          <div className="flex items-center gap-2">
            <BulkInviteDialog />
            <InviteUserDialog />
          </div>
        }
      />

      {/* Filter bar */}
      <div className="mb-4 flex flex-wrap items-center gap-2.5">
        <div className="relative min-w-[220px] flex-1">
          <Icon.search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('admin.users.list.searchPlaceholder')}
            className="pl-9"
            aria-label={t('admin.users.list.searchAria')}
          />
        </div>
        <FilterSelect
          label={t('admin.users.list.filterByRole')}
          value={role}
          onChange={(v) => setRole(v as 'all' | UserRole)}
          options={[
            { value: 'all', label: t('admin.users.list.allRoles') },
            { value: 'referee', label: t('admin.users.list.roleReferees') },
            { value: 'federation_admin', label: t('admin.users.list.roleFederationAdmins') },
            { value: 'super_admin', label: t('admin.users.list.roleProRefAdmins') },
          ]}
        />
        {/* Both only appear once there are at least two to choose between — a single-tenant
            platform gains nothing from a dropdown that can only mean "all" or "the only one". */}
        {federationOptions.length > 1 && (
          <FilterSelect
            label={t('admin.exams.list.filterByFederation')}
            value={federationId}
            onChange={setFederationId}
            options={[{ value: 'all', label: t('admin.clips.superAdminList.allFederations') }, ...federationOptions]}
          />
        )}
        {sportOptions.length > 1 && (
          <FilterSelect
            label={t('admin.exams.list.filterBySport')}
            value={sportId}
            onChange={setSportId}
            options={[{ value: 'all', label: t('admin.exams.list.allSports') }, ...sportOptions]}
          />
        )}
      </div>

      {isLoading ? (
        <ListSkeleton rows={6} />
      ) : isError ? (
        <div className="rounded border border-danger/40 bg-danger/10 px-4 py-3 text-[13px] text-danger">
          {apiErrorMessage(error, t('admin.users.list.couldNotLoad'))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded border border-dashed border-line-2 bg-bg-2 p-8 text-center">
          <p className="text-sm text-muted">
            {users.length === 0 ? t('admin.users.list.noUsersYet') : t('admin.users.list.noMatches')}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="overflow-x-auto rounded border border-line bg-surface">
            <table className="w-full text-left text-[13px]">
              <thead>
                <tr className="border-b border-line text-[11px] uppercase tracking-[0.05em] text-muted">
                  <th className="px-4 py-2.5 font-semibold">{t('admin.users.list.colUser')}</th>
                  <th className="px-4 py-2.5 font-semibold">{t('admin.users.list.colRole')}</th>
                  <th className="px-4 py-2.5 font-semibold">{t('admin.users.list.colFederation')}</th>
                  <th className="px-4 py-2.5 font-semibold">{t('admin.users.list.colSport')}</th>
                  <th className="px-4 py-2.5 font-semibold">{t('admin.users.list.colXp')}</th>
                  <th className="px-4 py-2.5 font-semibold">{t('admin.users.list.colStreak')}</th>
                  <th className="px-4 py-2.5 font-semibold">{t('admin.users.list.colJoined')}</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {pageItems.map((u: AdminUserListItem) => {
                  const meta = roleMeta(t, u.role);
                  return (
                    <tr key={u.id} className="group border-b border-line last:border-0">
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2.5">
                          <Avatar name={u.full_name} size="sm" />
                          <div className="min-w-0">
                            <div className="truncate font-medium text-ink">{u.full_name}</div>
                            <div className="truncate text-[12px] text-muted">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        <span
                          className={
                            'inline-flex h-[22px] items-center rounded-full border px-2.5 text-[11px] font-medium ' +
                            meta.chip
                          }
                        >
                          {meta.label}
                        </span>
                      </td>
                      {/* Platform staff (super_admin) has no tenant — show a dash, not an empty cell. */}
                      <td className="px-4 py-2.5 text-ink-2">{u.federation_name ?? '—'}</td>
                      <td className="px-4 py-2.5 text-ink-2">{u.sport_name ?? '—'}</td>
                      <td className="px-4 py-2.5 font-mono text-[12px] text-muted">{u.xp}</td>
                      <td className="px-4 py-2.5 font-mono text-[12px] text-muted">{u.current_streak}</td>
                      <td className="px-4 py-2.5 font-mono text-[12px] text-muted">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      {/* Row actions — subtle until hover so the table still reads as a roster
                          at a glance (same treatment as the federations table). */}
                      <td className="px-4 py-2.5">
                        <div className="flex items-center justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                          <EditUserDialog user={u} />
                          <RemoveUserDialog user={u} />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <DataPagination page={page} pageCount={pageCount} onPageChange={setPage} className="justify-end" />
        </div>
      )}
    </>
  );
}
